import { createAppRequest } from '@/services/request';
import type { ApiRequestOptions } from './types/common';
import type {
  AddMarketingCampaignMemberPayload,
  CreateMarketingCampaignPayload,
  MarketingCampaignDetail,
  MarketingCampaignItem,
  MarketingCampaignMemberPageResult,
  MarketingCampaignMemberQueryParams,
  MarketingCampaignTouchpointItem,
  MarketingCampaignTouchpointPageResult,
  MarketingCampaignTouchpointPayload,
  MarketingCampaignTouchpointQueryParams,
  MarketingCampaignPageResult,
  MarketingCampaignQueryParams,
} from './types/marketing-campaign';

const request = createAppRequest();
const BASE_PATH = '/bff/zy-crm/marketing-campaigns';

const isObject = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object';

const normalizePage = (resp: unknown): MarketingCampaignPageResult => {
  if (!isObject(resp)) {
    throw new Error('市场活动列表返回结构异常，请检查 API 地址或代理配置');
  }

  const root = resp as Record<string, unknown>;
  const candidates: unknown[] = [
    root,
    root.data,
    root.result,
    root.payload,
    (root.response as Record<string, unknown> | undefined)?.data,
  ];

  for (const candidate of candidates) {
    if (!isObject(candidate)) {
      continue;
    }
    const pageObj = candidate as Record<string, unknown>;
    const hasList = Array.isArray(pageObj.list);
    const hasItems = Array.isArray(pageObj.items);
    const hasDataArray = Array.isArray(pageObj.data);
    const data: unknown[] = hasList
      ? (pageObj.list as unknown[])
      : hasItems
        ? (pageObj.items as unknown[])
        : hasDataArray
          ? (pageObj.data as unknown[])
          : [];
    const totalRaw = pageObj.total ?? pageObj.count ?? data.length;
    const pageRaw = pageObj.page ?? pageObj.current ?? 1;
    const pageSizeRaw =
      (pageObj.pageSize ?? pageObj.limit ?? data.length) || 20;

    if (
      hasList ||
      hasItems ||
      hasDataArray ||
      pageObj.total !== undefined ||
      pageObj.count !== undefined
    ) {
      return {
        list: data as MarketingCampaignItem[],
        total: Number(totalRaw) || 0,
        page: Number(pageRaw) || 1,
        pageSize: Number(pageSizeRaw) || 20,
      };
    }
  }

  throw new Error('市场活动列表返回结构异常，请检查 API 地址或代理配置');
};

const normalizeTouchpointPage = (
  resp: unknown,
): MarketingCampaignTouchpointPageResult => {
  return normalizePage(resp) as MarketingCampaignTouchpointPageResult;
};

const normalizeMemberPage = (
  resp: unknown,
): MarketingCampaignMemberPageResult => {
  return normalizePage(resp) as MarketingCampaignMemberPageResult;
};

const unwrapDetail = (resp: unknown): MarketingCampaignDetail => {
  if (resp && typeof resp === 'object') {
    const obj = resp as Record<string, unknown>;
    const candidates = [
      obj,
      obj.data,
      obj.result,
      obj.payload,
      (obj.response as Record<string, unknown> | undefined)?.data,
    ];
    for (const candidate of candidates) {
      if (!candidate || typeof candidate !== 'object') {
        continue;
      }
      const record = candidate as Record<string, unknown>;
      if (typeof record.uuid === 'string') {
        return candidate as MarketingCampaignDetail;
      }
    }
  }
  throw new Error('市场活动详情返回结构异常');
};

export async function queryMarketingCampaigns(
  params: MarketingCampaignQueryParams,
  options?: ApiRequestOptions,
) {
  const resp = await request<unknown>(BASE_PATH, {
    method: 'GET',
    params,
    ...(options || {}),
  });
  return normalizePage(resp);
}

export async function queryMarketingCampaignDetail(
  uuid: string,
  options?: ApiRequestOptions,
) {
  const resp = await request<unknown>(`${BASE_PATH}/${uuid}`, {
    method: 'GET',
    ...(options || {}),
  });
  return unwrapDetail(resp);
}

export async function queryMarketingCampaignMembers(
  uuid: string,
  params: MarketingCampaignMemberQueryParams,
  options?: ApiRequestOptions,
) {
  const resp = await request<unknown>(`${BASE_PATH}/${uuid}/members`, {
    method: 'GET',
    params,
    ...(options || {}),
  });
  return normalizeMemberPage(resp);
}

export async function addMarketingCampaignMember(
  uuid: string,
  payload: AddMarketingCampaignMemberPayload,
  options?: ApiRequestOptions,
) {
  const resp = await request<unknown>(`${BASE_PATH}/${uuid}/members`, {
    method: 'POST',
    data: payload,
    ...(options || {}),
  });
  return unwrapDetail(resp);
}

export async function queryMarketingCampaignTouchpoints(
  uuid: string,
  params: MarketingCampaignTouchpointQueryParams,
  options?: ApiRequestOptions,
) {
  const resp = await request<unknown>(`${BASE_PATH}/${uuid}/touchpoints`, {
    method: 'GET',
    params,
    ...(options || {}),
  });
  return normalizeTouchpointPage(resp);
}

export async function createMarketingCampaignTouchpoint(
  uuid: string,
  payload: MarketingCampaignTouchpointPayload,
  options?: ApiRequestOptions,
) {
  const resp = await request<unknown>(`${BASE_PATH}/${uuid}/touchpoints`, {
    method: 'POST',
    data: payload,
    ...(options || {}),
  });
  return unwrapDetail(resp) as unknown as MarketingCampaignTouchpointItem;
}

export async function updateMarketingCampaignTouchpoint(
  uuid: string,
  touchpointUuid: string,
  payload: Partial<MarketingCampaignTouchpointPayload>,
  options?: ApiRequestOptions,
) {
  const resp = await request<unknown>(
    `${BASE_PATH}/${uuid}/touchpoints/${touchpointUuid}`,
    {
      method: 'PATCH',
      data: payload,
      ...(options || {}),
    },
  );
  return unwrapDetail(resp) as unknown as MarketingCampaignTouchpointItem;
}

export async function createMarketingCampaign(
  payload: CreateMarketingCampaignPayload,
  options?: ApiRequestOptions,
) {
  const resp = await request<unknown>(BASE_PATH, {
    method: 'POST',
    data: payload,
    ...(options || {}),
  });
  return unwrapDetail(resp);
}

export async function updateMarketingCampaign(
  uuid: string,
  payload: CreateMarketingCampaignPayload,
  options?: ApiRequestOptions,
) {
  const resp = await request<unknown>(`${BASE_PATH}/${uuid}`, {
    method: 'PATCH',
    data: payload,
    ...(options || {}),
  });
  return unwrapDetail(resp);
}

export async function closeMarketingCampaign(
  uuid: string,
  options?: ApiRequestOptions,
) {
  const resp = await request<unknown>(`${BASE_PATH}/${uuid}/close`, {
    method: 'POST',
    ...(options || {}),
  });
  return unwrapDetail(resp);
}

export async function deleteMarketingCampaign(
  uuid: string,
  options?: ApiRequestOptions,
) {
  return request<{ uuid: string; isDeleted: boolean }>(
    `${BASE_PATH}/${uuid}`,
    {
      method: 'DELETE',
      ...(options || {}),
    },
  );
}
